// Rendering-only stand-ins for other players in a multiplayer room: one
// buildFisherman() rig per remote id, driven by network transforms instead
// of local input. No camera/input code touches this module.

import * as THREE from 'three';
import { buildFisherman } from '@entities/player';
import { standY, makeBoatMesh, makeCartMesh, BOAT_FLOAT_Y } from '@systems/building';
import { addRemoteShelterMesh } from '@economy/rest';
import type { RemoteScore } from '@economy/market';
import type { AvatarVisual } from '@entities/avatars';
import type { PathWaypoint, PlayerStats, Transform } from '@net/protocol';

const ZERO_STATS: PlayerStats = { caught: 0, species: 0, areas: 0, infra: 0, upgrades: 0, wood: 0 };

export interface RemotePlayer {
  id: string;
  name: string;
  fisher: THREE.Group;
  x: number;
  z: number;
  heading: number;
  path: PathWaypoint[];
  pathStartLocalMs: number;
  boatMesh: THREE.Group | null;
  cartMesh: THREE.Group | null;
  score: number;
  stats: PlayerStats;
}

export const remotePlayerGroup = new THREE.Group();
export const remoteVehicleGroup = new THREE.Group();
export const remoteShelterGroup = new THREE.Group();

const remotePlayers = new Map<string, RemotePlayer>();

function angleDiff(a: number, b: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

export function upsertRemotePlayer(id: string, name: string, visual: AvatarVisual): RemotePlayer {
  const existing = remotePlayers.get(id);
  if (existing) {
    existing.name = name;
    return existing;
  }
  const { fisher } = buildFisherman(visual);
  const rp: RemotePlayer = {
    id, name, fisher, x: 0, z: 0, heading: 0, path: [], pathStartLocalMs: performance.now(),
    boatMesh: null, cartMesh: null, score: 0, stats: { ...ZERO_STATS },
  };
  remotePlayerGroup.add(fisher);
  remotePlayers.set(id, rp);
  return rp;
}

export function removeRemotePlayer(id: string): void {
  const rp = remotePlayers.get(id);
  if (!rp) return;
  remotePlayerGroup.remove(rp.fisher);
  if (rp.boatMesh) remoteVehicleGroup.remove(rp.boatMesh);
  if (rp.cartMesh) remoteVehicleGroup.remove(rp.cartMesh);
  remotePlayers.delete(id);
}

// Stores a freshly-received batch of waypoints to replay over the next few
// seconds. `waypoints[].t` is milliseconds since the *sender's* batch
// start, not a wall-clock timestamp — performance.now() epochs aren't
// comparable across tabs/machines, so rather than aligning clocks we just
// start replaying the batch now, over its own recorded duration. That
// means playback trails the sender by roughly one send interval, which is
// an accepted, simple tradeoff for a hobby project.
export function applyRemotePath(id: string, waypoints: PathWaypoint[]): void {
  const rp = remotePlayers.get(id);
  if (!rp || waypoints.length === 0) return;
  rp.path = waypoints;
  rp.pathStartLocalMs = performance.now();
  if (waypoints.some((w) => w.boat) && !rp.boatMesh) {
    rp.boatMesh = makeBoatMesh();
    remoteVehicleGroup.add(rp.boatMesh);
  }
  if (waypoints.some((w) => w.cart) && !rp.cartMesh) {
    rp.cartMesh = makeCartMesh();
    remoteVehicleGroup.add(rp.cartMesh);
  }
}

export function addRemoteShelter(x: number, z: number, heading: number): void {
  remoteShelterGroup.add(addRemoteShelterMesh(x, z, heading));
}

export function setRemoteStats(id: string, base: number, stats: PlayerStats): void {
  const rp = remotePlayers.get(id);
  if (!rp) return;
  rp.score = base;
  rp.stats = stats;
}

export function remoteScores(): RemoteScore[] {
  return [...remotePlayers.values()].map((rp) => ({ name: rp.name, base: rp.score, ...rp.stats }));
}

interface Sample {
  x: number;
  z: number;
  heading: number;
  boat: Transform | null;
  cart: Transform | null;
}

function lerpTransform(a: Transform, b: Transform, f: number): Transform {
  return { x: a.x + (b.x - a.x) * f, z: a.z + (b.z - a.z) * f, heading: a.heading + angleDiff(a.heading, b.heading) * f };
}

function samplePath(rp: RemotePlayer, nowMs: number): Sample {
  if (rp.path.length === 0) return { x: rp.x, z: rp.z, heading: rp.heading, boat: null, cart: null };
  const elapsed = nowMs - rp.pathStartLocalMs;
  const last = rp.path[rp.path.length - 1];
  if (elapsed >= last.t) return last;
  let i = 0;
  while (i < rp.path.length - 1 && rp.path[i + 1].t < elapsed) i++;
  const a = rp.path[i];
  const b = rp.path[Math.min(i + 1, rp.path.length - 1)];
  const f = Math.min(1, Math.max(0, (elapsed - a.t) / Math.max(1, b.t - a.t)));
  return {
    x: a.x + (b.x - a.x) * f,
    z: a.z + (b.z - a.z) * f,
    heading: a.heading + angleDiff(a.heading, b.heading) * f,
    boat: a.boat && b.boat ? lerpTransform(a.boat, b.boat, f) : (a.boat ?? b.boat),
    cart: a.cart && b.cart ? lerpTransform(a.cart, b.cart, f) : (a.cart ?? b.cart),
  };
}

export function updateRemotePlayers(dt: number): void {
  const nowMs = performance.now();
  const t = Math.min(1, dt * 12);
  remotePlayers.forEach((rp) => {
    const sample = samplePath(rp, nowMs);
    rp.x += (sample.x - rp.x) * t;
    rp.z += (sample.z - rp.z) * t;
    rp.heading += angleDiff(rp.heading, sample.heading) * t;
    rp.fisher.position.set(rp.x, standY(rp.x, rp.z), rp.z);
    rp.fisher.rotation.y = -rp.heading;

    if (rp.boatMesh && sample.boat) {
      const bt = sample.boat;
      rp.boatMesh.position.x += (bt.x - rp.boatMesh.position.x) * t;
      rp.boatMesh.position.z += (bt.z - rp.boatMesh.position.z) * t;
      rp.boatMesh.position.y = BOAT_FLOAT_Y;
      rp.boatMesh.rotation.y += angleDiff(rp.boatMesh.rotation.y, -bt.heading) * t;
    }
    if (rp.cartMesh && sample.cart) {
      const ct = sample.cart;
      rp.cartMesh.position.x += (ct.x - rp.cartMesh.position.x) * t;
      rp.cartMesh.position.z += (ct.z - rp.cartMesh.position.z) * t;
      rp.cartMesh.position.y = standY(rp.cartMesh.position.x, rp.cartMesh.position.z);
      rp.cartMesh.rotation.y += angleDiff(rp.cartMesh.rotation.y, -ct.heading) * t;
    }
  });
}
